import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DollarSign, Copy, Check, LogOut, Mail, ExternalLink, Upload } from 'lucide-react';

function SubscriptionPage({ setIsAuthenticated }) {
  // Updated wallet addresses - Dec 2024 - FORCE DEPLOY v2
  const [copied, setCopied] = useState('');
  const [txHash, setTxHash] = useState('');
  const [selectedNetwork, setSelectedNetwork] = useState('SOL (USDC)');
  const [submitting, setSubmitting] = useState(false);
  const [proofImage, setProofImage] = useState(null);
  const [proofImagePreview, setProofImagePreview] = useState(null);
  const navigate = useNavigate();

  const walletAddresses = {
    sol_usdc: "FDR26YGZWRA2x3XngAa2pj65cfPMSqkpYVkMNyqWNxy9",
    sol_usdt: "FDR26YGZWRA2x3XngAa2pj65cfPMSqkpYVkMNyqWNxy9",
    eth_usdt: "0x56F45321Ff47B3f087aD92a37aE67237A1905018",
    bsc_usdt: "0x56F45321Ff47B3f087aD92a37aE67237A1905018"
  };

  // WISE Payment Info
  const wisePaymentLink = "https://wise.com/pay/me/kelvint17";
  const wiseQRCode = "https://customer-assets.emergentagent.com/job_68315e9a-4b80-4abe-8173-19489f886c6b/artifacts/7pm7q96a_Screenshot%202026-01-06%20at%2012.22.10%E2%80%AFAM.png";
  
  // Contact & Crypto.com Info
  const contactEmail = "NinjasOfSatoshi@proton.me";
  const cryptoComLink = "https://crypto.com/7vgawxhreh";
  const cryptoComRefCode = "7vgawxhreh";

  const copyToClipboard = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(label);
      setTimeout(() => setCopied(''), 2000);
    } catch (err) {
      const textArea = document.createElement("textarea");
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      textArea.remove();
      setCopied(label);
      setTimeout(() => setCopied(''), 2000);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('signals_token');
    localStorage.removeItem('signals_user_email');
    setIsAuthenticated(false);
    navigate('/login');
  };

  const handleSubmitPayment = async (e) => {
    e.preventDefault();
    
    // Validate: need at least transaction ID or screenshot
    if (!txHash.trim() && !proofImage) {
      alert('Please provide either a Transaction ID or upload a payment screenshot (or both)');
      return;
    }

    const token = localStorage.getItem('signals_token');
    
    if (!token) {
      alert('Please login first to submit payment proof');
      navigate('/login');
      return;
    }

    setSubmitting(true);
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      
      // Upload image if provided
      let imageUrl = null;
      if (proofImage) {
        const formData = new FormData();
        formData.append('file', proofImage);
        
        const uploadResponse = await fetch(`${BACKEND_URL}/api/signals-app/upload-payment-proof`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        });
        
        if (uploadResponse.ok) {
          const uploadResult = await uploadResponse.json();
          imageUrl = uploadResult.image_url;
        } else {
          throw new Error('Failed to upload payment proof image');
        }
      }
      
      const response = await fetch(`${BACKEND_URL}/api/signals-app/submit-payment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          network: selectedNetwork,
          transaction_hash: txHash || null,
          proof_image_url: imageUrl
        })
      });

      if (response.ok) {
        alert('Payment submitted successfully! Your subscription will be activated within 24 hours.');
        setTxHash('');
        setProofImage(null);
        setProofImagePreview(null);
        navigate('/signals');
      } else {
        const error = await response.json();
        if (error.detail === 'Invalid token' || error.detail === 'Not authenticated') {
          alert('Session expired. Please login again.');
          localStorage.removeItem('token');
          navigate('/login');
        } else {
          alert(error.detail || 'Failed to submit payment');
        }
      }
    } catch (error) {
      console.error('Error submitting payment:', error);
      alert('Failed to submit payment. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert('Image size must be less than 5MB');
        return;
      }
      setProofImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setProofImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <div style={{ minHeight: '100vh', padding: '2rem 1rem' }}>
      <div className="container" style={{ maxWidth: '600px' }}>
        {/* Logout Button */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' }}>
          <button
            onClick={handleLogout}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.5rem 1rem',
              background: 'transparent',
              border: '1px solid #334155',
              borderRadius: '0.375rem',
              color: '#94a3b8',
              cursor: 'pointer',
              fontSize: '0.875rem'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = '#ef4444';
              e.currentTarget.style.color = '#ef4444';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = '#334155';
              e.currentTarget.style.color = '#94a3b8';
            }}
          >
            <LogOut style={{ width: '16px', height: '16px' }} />
            Logout
          </button>
        </div>

        {/* Early Adopters Banner */}
        <div style={{
          background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
          borderRadius: '1rem',
          padding: '1.5rem',
          marginBottom: '1.5rem',
          textAlign: 'center',
          boxShadow: '0 10px 40px rgba(16, 185, 129, 0.3)'
        }}>
          <div style={{ fontSize: '0.9rem', color: 'rgba(255,255,255,0.9)', marginBottom: '0.5rem', fontWeight: '600' }}>
            🚀 EARLY ADOPTERS LAUNCH
          </div>
          <div style={{ fontSize: '2.25rem', fontWeight: 'bold', color: 'white', marginBottom: '0.25rem' }}>
            22 DAYS FREE
          </div>
          <div style={{ fontSize: '1rem', color: 'rgba(255,255,255,0.9)' }}>
            Limited Time Only!
          </div>
          <div style={{ 
            marginTop: '1rem', 
            padding: '0.5rem 1rem', 
            background: 'rgba(255,255,255,0.2)', 
            borderRadius: '0.5rem',
            display: 'inline-block'
          }}>
            <span style={{ color: 'white', fontSize: '0.95rem' }}>Then just <strong>$10/month</strong> after trial</span>
          </div>
        </div>

        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <h1 style={{ fontSize: '1.75rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>Subscribe to Savage AI Signals</h1>
          <p style={{ color: '#94a3b8' }}>Pay now to secure your spot • Free trial starts immediately!</p>
        </div>

        {/* Important Disclaimer */}
        <div style={{ 
          background: 'rgba(251, 191, 36, 0.1)', 
          border: '1px solid rgba(251, 191, 36, 0.4)',
          borderRadius: '0.5rem',
          padding: '1rem',
          marginBottom: '1.5rem',
          fontSize: '0.8rem',
          lineHeight: '1.6'
        }}>
          <div style={{ fontWeight: 'bold', color: '#fbbf24', marginBottom: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem' }}>
            ⚠️ IMPORTANT DISCLAIMER
          </div>
          <p style={{ color: '#cbd5e1', marginBottom: '0.5rem' }}>
            The information provided by Savage AI Signals is <strong style={{ color: '#fbbf24' }}>NOT financial advice</strong>. 
            All signals, analyses, and content are based solely on <strong>technical analysis (TA)</strong> and <strong>probability indicators</strong> — not certainties.
          </p>
          <p style={{ color: '#cbd5e1', marginBottom: '0.5rem' }}>
            <strong style={{ color: '#ef4444' }}>There are NO guarantees</strong> of making money or achieving any specific returns. Trading cryptocurrencies, stocks, and other assets involves substantial risk of loss, including the potential loss of your entire investment.
          </p>
          <p style={{ color: '#cbd5e1' }}>
            By subscribing, you acknowledge that you have read and understood this disclaimer, accept full responsibility for your own trading decisions, and agree not to hold Savage AI Signals liable for any losses.
          </p>
        </div>

        <div className="card">
          <h2 style={{ fontSize: '1.25rem', fontWeight: 'bold', marginBottom: '1rem' }}>
            <DollarSign size={20} style={{ display: 'inline', marginRight: '0.5rem' }} />
            Monthly Subscription
          </h2>
          
          <div style={{ background: 'rgba(16, 185, 129, 0.1)', border: '1px solid rgba(16, 185, 129, 0.3)', borderRadius: '0.5rem', padding: '1rem', marginBottom: '1.5rem' }}>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#10b981', marginBottom: '0.25rem' }}>$10/month</div>
            <div style={{ fontSize: '0.875rem', color: '#fbbf24' }}>🎉 Early Adopter Special Price!</div>
            <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: '0.25rem' }}>Pay in USDT or USDC</div>
          </div>

          <h3 style={{ fontSize: '1rem', fontWeight: 'bold', marginBottom: '1rem' }}>What You Get:</h3>
          <ul style={{ marginBottom: '1.5rem', paddingLeft: '1.5rem', color: '#94a3b8' }}>
            <li style={{ marginBottom: '0.5rem' }}>✅ Real-time market scanner updates</li>
            <li style={{ marginBottom: '0.5rem' }}>✅ Individual signal analysis with ideal entry points</li>
            <li style={{ marginBottom: '0.5rem' }}>✅ Stop-loss and take-profit levels</li>
            <li style={{ marginBottom: '0.5rem' }}>✅ Market commentary and insights</li>
            <li style={{ marginBottom: '0.5rem' }}>✅ Unlimited access to all signals</li>
          </ul>

          <h3 style={{ fontSize: '1rem', fontWeight: 'bold', marginBottom: '1rem' }}>Payment Instructions:</h3>
          
          <div style={{ marginBottom: '1rem', padding: '1rem', background: '#0f172a', borderRadius: '0.5rem' }}>
            <div style={{ fontSize: '0.875rem', fontWeight: 'bold', marginBottom: '0.5rem', color: '#10b981' }}>
              SOL Network (USDC or USDT)
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#1e293b', padding: '0.75rem', borderRadius: '0.375rem' }}>
              <span style={{ fontSize: '0.75rem', wordBreak: 'break-all', marginRight: '0.5rem' }}>{walletAddresses.sol_usdc}</span>
              <button
                onClick={() => copyToClipboard(walletAddresses.sol_usdc, 'sol')}
                style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#10b981' }}
              >
                {copied === 'sol' ? <Check size={20} /> : <Copy size={20} />}
              </button>
            </div>
          </div>

          <div style={{ marginBottom: '1.5rem', padding: '1rem', background: '#0f172a', borderRadius: '0.5rem' }}>
            <div style={{ fontSize: '0.875rem', fontWeight: 'bold', marginBottom: '0.25rem', color: '#10b981' }}>
              EVM Networks (USDT only)
            </div>
            <div style={{ fontSize: '0.7rem', color: '#94a3b8', marginBottom: '0.5rem' }}>
              ETH, BSC, ARB, AVAX, POL, BASE, LINEA, OP, ZKSync, ZORA
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#1e293b', padding: '0.75rem', borderRadius: '0.375rem' }}>
              <span style={{ fontSize: '0.75rem', wordBreak: 'break-all', marginRight: '0.5rem' }}>{walletAddresses.eth_usdt}</span>
              <button
                onClick={() => copyToClipboard(walletAddresses.eth_usdt, 'evm')}
                style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#10b981' }}
              >
                {copied === 'evm' ? <Check size={20} /> : <Copy size={20} />}
              </button>
            </div>
          </div>

          {/* WISE Cash/USD Payment Option */}
          <div style={{ marginBottom: '1.5rem', padding: '1.25rem', background: 'linear-gradient(135deg, #0f172a 0%, #1a2744 100%)', borderRadius: '0.75rem', border: '1px solid #3b82f6' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
              <span style={{ fontSize: '1.5rem' }}>💵</span>
              <div style={{ fontSize: '1rem', fontWeight: 'bold', color: '#3b82f6' }}>
                Pay with Cash/USD via WISE
              </div>
            </div>
            <div style={{ fontSize: '0.85rem', color: '#94a3b8', marginBottom: '1rem' }}>
              Equivalent to USDT value (e.g., $10 USDT = $10 USD)
            </div>
            
            {/* QR Code */}
            <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
              <img 
                src={wiseQRCode} 
                alt="WISE Payment QR Code" 
                style={{ 
                  width: '180px', 
                  height: '180px', 
                  borderRadius: '0.5rem',
                  background: 'white',
                  padding: '0.5rem'
                }} 
              />
              <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '0.5rem' }}>
                Scan QR code with WISE app
              </div>
            </div>
            
            {/* Payment Link */}
            <div style={{ marginBottom: '1rem' }}>
              <div style={{ fontSize: '0.8rem', color: '#94a3b8', marginBottom: '0.5rem' }}>Or use payment link:</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', background: '#1e293b', padding: '0.75rem', borderRadius: '0.375rem' }}>
                <a 
                  href={wisePaymentLink} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  style={{ 
                    flex: 1, 
                    color: '#3b82f6', 
                    fontSize: '0.8rem', 
                    wordBreak: 'break-all',
                    textDecoration: 'none'
                  }}
                >
                  {wisePaymentLink}
                </a>
                <button
                  onClick={() => copyToClipboard(wisePaymentLink, 'wise')}
                  style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#3b82f6', flexShrink: 0 }}
                >
                  {copied === 'wise' ? <Check size={18} /> : <Copy size={18} />}
                </button>
                <a 
                  href={wisePaymentLink} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  style={{ color: '#3b82f6', flexShrink: 0 }}
                >
                  <ExternalLink size={18} />
                </a>
              </div>
            </div>
            
            {/* Important Reminder for WISE payments */}
            <div style={{ 
              background: 'rgba(251, 191, 36, 0.15)', 
              border: '1px solid rgba(251, 191, 36, 0.4)',
              borderRadius: '0.5rem',
              padding: '0.75rem',
              fontSize: '0.8rem',
              color: '#fbbf24'
            }}>
              <strong>📸 IMPORTANT:</strong> After payment, take a screenshot of your WISE receipt with the <strong>Transaction ID visible</strong>. Keep this as proof of payment in case of any issues.
            </div>
          </div>

          {/* How to Buy Crypto - Crypto.com */}
          <div style={{ marginBottom: '1.5rem', padding: '1.25rem', background: 'linear-gradient(135deg, #0f172a 0%, #1a2744 100%)', borderRadius: '0.75rem', border: '1px solid #8b5cf6' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
              <span style={{ fontSize: '1.5rem' }}>🪙</span>
              <div style={{ fontSize: '1rem', fontWeight: 'bold', color: '#8b5cf6' }}>
                New to Crypto? Buy USDT via Crypto.com
              </div>
            </div>
            <div style={{ fontSize: '0.85rem', color: '#94a3b8', marginBottom: '1rem', lineHeight: '1.6' }}>
              If you don't have crypto yet, you can easily buy USDT using the Crypto.com app:
            </div>
            
            <ol style={{ fontSize: '0.85rem', color: '#cbd5e1', paddingLeft: '1.25rem', marginBottom: '1rem', lineHeight: '1.8' }}>
              <li style={{ marginBottom: '0.5rem' }}>
                Download the <strong>Crypto.com app</strong> or click the link below to join
              </li>
              <li style={{ marginBottom: '0.5rem' }}>
                When prompted, enter invite/referral code: <strong style={{ color: '#8b5cf6' }}>{cryptoComRefCode}</strong>
              </li>
              <li style={{ marginBottom: '0.5rem' }}>
                Complete verification (KYC)
              </li>
              <li style={{ marginBottom: '0.5rem' }}>
                Buy USDT with your credit/debit card or bank transfer
              </li>
              <li>
                Send USDT to our wallet address above
              </li>
            </ol>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <a 
                href={cryptoComLink} 
                target="_blank" 
                rel="noopener noreferrer"
                style={{ 
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                  background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
                  color: 'white',
                  padding: '0.75rem 1rem',
                  borderRadius: '0.5rem',
                  textDecoration: 'none',
                  fontWeight: '600',
                  fontSize: '0.9rem'
                }}
              >
                <ExternalLink size={18} />
                Join Crypto.com
              </a>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', background: '#1e293b', padding: '0.75rem', borderRadius: '0.375rem' }}>
                <span style={{ flex: 1, fontSize: '0.8rem', color: '#94a3b8' }}>Referral Code:</span>
                <span style={{ fontWeight: 'bold', color: '#8b5cf6', fontSize: '0.9rem' }}>{cryptoComRefCode}</span>
                <button
                  onClick={() => copyToClipboard(cryptoComRefCode, 'refcode')}
                  style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#8b5cf6' }}
                >
                  {copied === 'refcode' ? <Check size={18} /> : <Copy size={18} />}
                </button>
              </div>
            </div>
          </div>

          <div style={{ marginTop: '2rem', padding: '1.5rem', background: '#0f172a', borderRadius: '0.5rem', border: '1px solid rgba(16, 185, 129, 0.3)' }}>
            <h3 style={{ fontSize: '1rem', fontWeight: 'bold', marginBottom: '1rem', color: '#10b981' }}>
              Step 2: Submit Your Payment Proof
            </h3>
            
            <form onSubmit={handleSubmitPayment}>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: '0.875rem', marginBottom: '0.5rem', color: '#94a3b8' }}>
                  Payment Method Used
                </label>
                <select
                  value={selectedNetwork}
                  onChange={(e) => {
                    setSelectedNetwork(e.target.value);
                    setTxHash('');
                    setProofImage(null);
                    setProofImagePreview(null);
                  }}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    background: '#1e293b',
                    border: '1px solid #334155',
                    borderRadius: '0.375rem',
                    color: '#e2e8f0',
                    fontSize: '0.875rem'
                  }}
                >
                  <option value="SOL (USDC)">Solana - USDC</option>
                  <option value="SOL (USDT)">Solana - USDT</option>
                  <option value="ETH (USDT)">Ethereum - USDT</option>
                  <option value="BSC (USDT)">BSC - USDT</option>
                  <option value="WISE">WISE (Cash/USD)</option>
                </select>
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: '0.875rem', marginBottom: '0.5rem', color: '#94a3b8' }}>
                  Transaction ID / Reference Number {selectedNetwork !== 'WISE' ? '' : '(from receipt)'}
                </label>
                <input
                  type="text"
                  value={txHash}
                  onChange={(e) => setTxHash(e.target.value)}
                  placeholder={selectedNetwork === 'WISE' ? "Enter WISE transaction/reference number..." : "Paste your transaction hash here..."}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    background: '#1e293b',
                    border: '1px solid #334155',
                    borderRadius: '0.375rem',
                    color: '#e2e8f0',
                    fontSize: '0.875rem'
                  }}
                />
                <p style={{ fontSize: '0.7rem', color: '#64748b', marginTop: '0.25rem' }}>
                  {selectedNetwork === 'WISE' ? 'Optional if uploading screenshot' : 'Optional if uploading screenshot'}
                </p>
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: '0.875rem', marginBottom: '0.5rem', color: '#94a3b8' }}>
                  📸 Upload Payment Screenshot
                </label>
                <div 
                  style={{
                    border: '2px dashed #334155',
                    borderRadius: '0.5rem',
                    padding: '1.5rem',
                    textAlign: 'center',
                    cursor: 'pointer',
                    background: proofImagePreview ? 'transparent' : '#1e293b',
                    transition: 'all 0.2s'
                  }}
                  onClick={() => document.getElementById('proof-image-input').click()}
                  onDragOver={(e) => { e.preventDefault(); e.currentTarget.style.borderColor = '#10b981'; }}
                  onDragLeave={(e) => { e.currentTarget.style.borderColor = '#334155'; }}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.currentTarget.style.borderColor = '#334155';
                    const file = e.dataTransfer.files[0];
                    if (file && file.type.startsWith('image/')) {
                      handleImageSelect({ target: { files: [file] } });
                    }
                  }}
                >
                  {proofImagePreview ? (
                    <div>
                      <img 
                        src={proofImagePreview} 
                        alt="Payment proof" 
                        style={{ 
                          maxWidth: '100%', 
                          maxHeight: '200px', 
                          borderRadius: '0.375rem',
                          marginBottom: '0.5rem'
                        }} 
                      />
                      <p style={{ fontSize: '0.75rem', color: '#10b981' }}>✓ Image selected - Click to change</p>
                    </div>
                  ) : (
                    <div>
                      <Upload size={32} style={{ color: '#64748b', marginBottom: '0.5rem' }} />
                      <p style={{ color: '#94a3b8', fontSize: '0.875rem' }}>
                        Click or drag & drop your payment screenshot
                      </p>
                      <p style={{ color: '#64748b', fontSize: '0.75rem', marginTop: '0.25rem' }}>
                        PNG, JPG up to 5MB
                      </p>
                    </div>
                  )}
                </div>
                <input
                  id="proof-image-input"
                  type="file"
                  accept="image/*"
                  onChange={handleImageSelect}
                  style={{ display: 'none' }}
                />
                <p style={{ fontSize: '0.7rem', color: '#64748b', marginTop: '0.25rem' }}>
                  {selectedNetwork === 'WISE' ? 'Recommended for WISE payments' : 'Optional if providing transaction ID'}
                </p>
              </div>

              <div style={{ 
                background: 'rgba(251, 191, 36, 0.1)', 
                border: '1px solid rgba(251, 191, 36, 0.3)',
                borderRadius: '0.375rem',
                padding: '0.75rem',
                marginBottom: '1rem',
                fontSize: '0.75rem',
                color: '#fbbf24'
              }}>
                ⚠️ Please provide at least a Transaction ID OR a Screenshot (or both) for faster verification.
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="button button-primary"
                style={{ width: '100%', opacity: submitting ? 0.5 : 1 }}
              >
                {submitting ? 'Submitting...' : 'Submit Payment Proof'}
              </button>
            </form>

            <p style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '1rem', textAlign: 'center' }}>
              Your subscription will be activated within 24 hours after verification
            </p>
          </div>

          <button
            className="button button-secondary"
            onClick={() => navigate('/signals')}
            style={{ width: '100%', marginTop: '1rem' }}
          >
            Back to Signals
          </button>

          {/* Contact Support Section */}
          <div style={{ 
            marginTop: '1.5rem', 
            padding: '1rem', 
            background: 'rgba(59, 130, 246, 0.1)', 
            border: '1px solid rgba(59, 130, 246, 0.3)',
            borderRadius: '0.5rem',
            textAlign: 'center'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
              <Mail size={18} style={{ color: '#3b82f6' }} />
              <span style={{ fontSize: '0.9rem', fontWeight: '600', color: '#e2e8f0' }}>Need Help or Have Questions?</span>
            </div>
            <div style={{ fontSize: '0.85rem', color: '#94a3b8', marginBottom: '0.75rem' }}>
              Contact us for payment issues or any inquiries:
            </div>
            <a 
              href={`mailto:${contactEmail}`}
              style={{ 
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                color: '#3b82f6', 
                fontSize: '0.95rem',
                fontWeight: '600',
                textDecoration: 'none'
              }}
            >
              {contactEmail}
              <button
                onClick={(e) => { e.preventDefault(); copyToClipboard(contactEmail, 'email'); }}
                style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#3b82f6' }}
              >
                {copied === 'email' ? <Check size={16} /> : <Copy size={16} />}
              </button>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SubscriptionPage;
